/**
 * DV-STAGE-003: Session outputs management
 * Handles storing and retrieving stage outputs from test runs in the session
 */

import { SESSION_STORAGE_KEYS, CACHE_SETTINGS, StageId } from '@/types/stage-tester';

interface SessionOutput {
  stage: string;
  output: any;
  artifacts?: any[];
  executionId?: string;
  iterationId?: string;
  completedAt: string;
  requestId?: string;
}

class SessionOutputsManager {
  private outputs: Map<string, SessionOutput> = new Map();

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Store output from a successful stage run
   */
  storeOutput(stage: string, data: {
    output: any;
    artifacts?: any[];
    executionId?: string;
    iterationId?: string;
    requestId?: string;
  }) {
    const sessionOutput: SessionOutput = {
      stage,
      output: data.output,
      artifacts: data.artifacts,
      executionId: data.executionId,
      iterationId: data.iterationId,
      completedAt: new Date().toISOString(),
      requestId: data.requestId
    };

    this.outputs.set(stage, sessionOutput);
    this.persistToStorage();

    console.log(`📦 Stored session output for ${stage}:`, {
      outputSize: JSON.stringify(data.output).length,
      hasArtifacts: !!data.artifacts?.length,
      executionId: data.executionId,
      requestId: data.requestId
    });
  }

  /**
   * Get the last output for a stage
   */
  getOutput(stage: string): SessionOutput | null {
    return this.outputs.get(stage) || null;
  }

  /**
   * Get the last output for the previous stage (for last_output input source)
   */
  getLastOutputForStage(currentStage: StageId): SessionOutput | null {
    // Map current stage to its dependency
    const dependencies: Record<StageId, StageId | null> = {
      script: null,
      vision: 'script',
      storyboard: 'vision',
      frames: 'storyboard',
      video: 'frames',
      qc: 'video',
      export: 'qc',
      music: 'video' // Music can use video output
    };

    const previousStage = dependencies[currentStage];
    if (!previousStage) return null;

    return this.getOutput(previousStage);
  }

  /**
   * Check if we have a recent output for a stage
   */
  hasRecentOutput(stage: string, maxAgeMs: number = 60 * 60 * 1000): boolean {
    const output = this.getOutput(stage);
    if (!output) return false;

    const age = Date.now() - new Date(output.completedAt).getTime();
    return age <= maxAgeMs;
  }

  /**
   * Clear all session outputs
   */
  clearAll() {
    this.outputs.clear();
    this.persistToStorage();
  }

  /**
   * Clear output for a specific stage
   */
  clearStage(stage: string) {
    this.outputs.delete(stage);
    this.persistToStorage();
  }

  /**
   * Get all stored outputs for debugging
   */
  getAllOutputs(): Record<string, SessionOutput> {
    const result: Record<string, SessionOutput> = {};
    this.outputs.forEach((output, stage) => {
      result[stage] = output;
    });
    return result;
  }

  /**
   * Get summary of stored outputs
   */
  getOutputsSummary(): Record<string, { size: number; age: string; executionId?: string }> {
    const summary: Record<string, any> = {};
    const now = Date.now();

    this.outputs.forEach((output, stage) => {
      const age = now - new Date(output.completedAt).getTime();
      const ageStr = age < 60000 ? `${Math.round(age / 1000)}s` :
                     age < 3600000 ? `${Math.round(age / 60000)}m` :
                     `${Math.round(age / 3600000)}h`;

      summary[stage] = {
        size: JSON.stringify(output.output).length,
        age: ageStr,
        executionId: output.executionId
      };
    });

    return summary;
  }

  /**
   * Load outputs from sessionStorage
   */
  private loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionOutputs);
      if (stored) {
        const data = JSON.parse(stored);
        this.outputs = new Map(Object.entries(data));
      }
    } catch (error) {
      console.warn('Failed to load session outputs from storage:', error);
      this.outputs.clear();
    }
  }

  /**
   * Persist outputs to sessionStorage
   */
  private persistToStorage() {
    try {
      // Keep only the most recent outputs (limit size)
      const entries = Array.from(this.outputs.entries());
      if (entries.length > CACHE_SETTINGS.sessionOutputs.maxEntries) {
        // Sort by completion time and keep the most recent
        entries.sort((a, b) =>
          new Date(b[1].completedAt).getTime() - new Date(a[1].completedAt).getTime()
        );

        this.outputs = new Map(entries.slice(0, CACHE_SETTINGS.sessionOutputs.maxEntries));
      }

      const data = Object.fromEntries(this.outputs);
      sessionStorage.setItem(SESSION_STORAGE_KEYS.sessionOutputs, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to persist session outputs to storage:', error);
    }
  }
}

// Singleton instance
export const sessionOutputs = new SessionOutputsManager();